// src/tabs/logements/ImportPreviewModal.jsx — Phase 2.E.7 (ADR-040)
// Modale de preview de l'import Beds24 (consommée par LogementWebsiteTab —
// WAGON R Lot R3, ex-SyncBeds24Tab supprimé Lot R4).
// Affiche un tableau "champ FBZ V2 actuel" vs "valeur Beds24 récupérée"
// avec une case à cocher par champ. L'admin valide quoi importer.

import { useState, useEffect, useMemo } from "react"
import { DS } from "../../lib/ui"
import { valuesDiffer } from "../../lib/beds24-listings"

// Liste des champs candidats à l'import (label + getter sur listing/log)
// WAGON R5 Lot 2 (D4) — import RECENTRÉ sur DESCRIPTION + ÉQUIPEMENTS uniquement.
// Capacité / tarifs / horaires / adresse publique RETIRÉS des importables
// (décision B Dimitri) : ces données sont désormais consommées depuis l'app
// (Info FBZ / pricing / horaires admin), pas pré-remplies depuis Beds24.
//
// • `description` : la valeur Beds24 (`public_description`) PRÉ-REMPLIT le champ
//   description FR du site (state `descriptionFr` de LogementWebsiteTab, source
//   `logement_site_content`) — elle N'ÉCRIT PLUS le cfg orphelin
//   `public_description`. L'admin relit/ajuste puis Enregistre normalement
//   (traduction auto EN/ES au save). Le câblage est fait côté parent
//   (handleApplyImport déstructure `description` du patch → setDescriptionFr).
// • `equipements` : continue d'écrire le cfg via onUpdate/onUpdateBatch (inchangé).
//
// `public_title` reste hors import (champ cfg orphelin post-D-3, pas d'éditeur).
const FIELDS = [
  { id: 'description',  label: 'Description', pick: l => l.public_description, render: v => v ? (String(v).length > 160 ? `${String(v).slice(0,160)}…` : String(v)) : '—' },
  { id: 'equipements',  label: 'Équipements', pick: l => l.equipements, render: v => Array.isArray(v) ? `${v.length} coché${v.length>1?'s':''} : ${v.slice(0,4).join(', ')}${v.length>4?'…':''}` : '—' },
]

export default function ImportPreviewModal({ log, listing, rawData, onClose, onApply }) {
  // Détermine pour chaque champ s'il y a une vraie différence (sinon décoché par défaut)
  const initialChecked = useMemo(() => {
    const m = {}
    for (const f of FIELDS) {
      const cur = f.pick(log)
      const fet = f.pick(listing)
      // Pré-coche uniquement s'il y a une vraie différence ET que la valeur fetched n'est pas vide
      const hasFetched = fet !== null && fet !== undefined && fet !== '' && !(Array.isArray(fet) && fet.length === 0)
      m[f.id] = hasFetched && valuesDiffer(cur, fet)
    }
    return m
  }, [log, listing])

  const [checked, setChecked] = useState(initialChecked)

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const checkedCount = Object.values(checked).filter(Boolean).length

  // LOT ÉQUIPEMENTS (audit 2026-06-11) — codes Beds24 non reconnus (hors bruit
  // structurel) surfacés par fetchListingFromBeds24. Lecture seule : on informe
  // l'admin qu'un signal est perdu à l'import, sans bloquer ni cocher quoi que
  // ce soit. Champ additif `equipements_ignored` (peut être absent → garde).
  const ignoredEquipements = Array.isArray(listing?.equipements_ignored)
    ? listing.equipements_ignored
    : []

  function toggle(id) { setChecked(c => ({ ...c, [id]: !c[id] })) }
  function checkAll() { setChecked(Object.fromEntries(FIELDS.map(f => [f.id, true]))) }
  function uncheckAll() { setChecked({}) }

  function handleApply() {
    // WAGON R5 Lot 2 (D4) — l'import ne porte plus que `description` (string) et
    // `equipements` (array d'ids). Valeurs directes, aucun merge d'objet partiel.
    // Le routage description→state FR / equipements→cfg est fait côté parent
    // (handleApplyImport), qui sait ne PAS écrire le cfg public_description.
    const patch = {}
    for (const f of FIELDS) {
      if (!checked[f.id]) continue
      const fet = f.pick(listing)
      if (fet === null || fet === undefined) continue
      patch[f.id] = fet
    }
    onApply(patch, Object.keys(patch))
  }

  return (
    <div onClick={onClose}
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",padding:16,fontFamily:DS.fontBody}}>
      <div onClick={e => e.stopPropagation()}
        className="fbz-modal-sheet"
        style={{background:DS.white,borderRadius:12,maxWidth:780,width:"100%",maxHeight:"85vh",overflowY:"auto",padding:0,display:"flex",flexDirection:"column"}}>
        {/* Header */}
        <div style={{padding:"16px 20px",borderBottom:"1px solid #eef2f7",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontFamily:DS.fontDisplay,fontSize:17,fontWeight:700,color:DS.navy}}>
              Aperçu de l'import Beds24
            </div>
            <div style={{fontSize:11,color:"#888",marginTop:2}}>
              Cocher les champs à remplacer par la valeur récupérée. Les champs identiques sont décochés par défaut.
            </div>
          </div>
          <button onClick={onClose} style={{background:"transparent",border:"none",fontSize:22,cursor:"pointer",color:"#888"}}>×</button>
        </div>

        {/* Bulk actions */}
        <div style={{padding:"8px 20px",borderBottom:"1px solid #f4f7fb",background:"#f8fafd",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:12,color:"#555"}}>
            <strong style={{color:DS.navy}}>{checkedCount}</strong> / {FIELDS.length} champ{checkedCount>1?'s':''} à importer
          </div>
          <div style={{display:"flex",gap:6}}>
            <button onClick={checkAll}
              style={{background:"#fff",border:"1px solid #dde3ec",color:DS.navy,borderRadius:6,padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:DS.fontBody}}>
              Tout cocher
            </button>
            <button onClick={uncheckAll}
              style={{background:"#fff",border:"1px solid #dde3ec",color:"#888",borderRadius:6,padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:DS.fontBody}}>
              Tout décocher
            </button>
          </div>
        </div>

        {/* Avertissement équipements Beds24 non reconnus (lecture seule, pas de blocage) */}
        {ignoredEquipements.length > 0 && (
          <div style={{margin:"8px 20px 0",padding:"8px 12px",background:"#fff8e6",border:"1px solid #f5d77a",borderRadius:7,fontSize:11.5,color:"#7a5a00",lineHeight:1.5}}>
            {ignoredEquipements.length} équipement{ignoredEquipements.length>1?'s':''} Beds24 non reconnu{ignoredEquipements.length>1?'s':''} (non importé{ignoredEquipements.length>1?'s':''}) : {ignoredEquipements.join(', ')}
          </div>
        )}

        {/* Tableau */}
        <div style={{flex:1,overflowY:"auto",padding:"4px 20px"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr style={{background:"#fff",position:"sticky",top:0,zIndex:1}}>
                <th style={{padding:"10px 6px",textAlign:"left",fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:.4,fontWeight:700,width:30}}></th>
                <th style={{padding:"10px 6px",textAlign:"left",fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:.4,fontWeight:700,width:130}}>Champ</th>
                <th style={{padding:"10px 6px",textAlign:"left",fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:.4,fontWeight:700}}>Actuel FBZ V2</th>
                <th style={{padding:"10px 6px",textAlign:"left",fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:.4,fontWeight:700}}>Récupéré Beds24</th>
              </tr>
            </thead>
            <tbody>
              {FIELDS.map(f => {
                const cur = f.pick(log)
                const fet = f.pick(listing)
                const renderFn = f.render || (v => v == null || v === '' ? '—' : String(v))
                const isDiff = valuesDiffer(cur, fet)
                const fetchedEmpty = fet === null || fet === undefined || fet === '' || (Array.isArray(fet) && fet.length === 0)
                return (
                  <tr key={f.id} style={{borderTop:"1px solid #f4f7fb",background: checked[f.id] ? "#f0f7ff" : "#fff"}}>
                    <td style={{padding:"10px 6px",verticalAlign:"top"}}>
                      <input type="checkbox" checked={!!checked[f.id]}
                        onChange={() => toggle(f.id)}
                        disabled={fetchedEmpty}
                        title={fetchedEmpty ? 'Beds24 ne renvoie pas de valeur pour ce champ' : ''}
                        style={{cursor: fetchedEmpty ? 'not-allowed' : 'pointer'}}/>
                    </td>
                    <td style={{padding:"10px 6px",verticalAlign:"top",fontWeight:600,color:DS.navy}}>{f.label}</td>
                    <td style={{padding:"10px 6px",verticalAlign:"top",color:"#444",wordBreak:"break-word"}}>
                      <div style={{maxHeight:80,overflowY:"auto"}}>{renderFn(cur)}</div>
                    </td>
                    <td style={{padding:"10px 6px",verticalAlign:"top",color: fetchedEmpty ? '#aaa' : (isDiff ? DS.green : '#444'),wordBreak:"break-word"}}>
                      <div style={{maxHeight:80,overflowY:"auto",fontStyle: fetchedEmpty ? 'italic' : 'normal'}}>
                        {fetchedEmpty ? '(non renvoyé par Beds24)' : renderFn(fet)}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {/* Debug — clés brutes Beds24 (utile si un champ remonte vide pour
              vérifier qu'un flag include* manque côté backend) */}
          {(rawData?.property_keys?.length || rawData?.room_keys?.length) ? (
            <details style={{marginTop:12,fontSize:11,color:"#666",borderTop:"1px solid #eef",paddingTop:8}}>
              <summary style={{cursor:"pointer"}}>🔧 Données techniques (debug)</summary>
              <div style={{marginTop:8,fontFamily:"monospace",fontSize:10,lineHeight:1.5}}>
                {rawData?.property_keys?.length ? (
                  <div><strong>property keys :</strong> {rawData.property_keys.join(", ")}</div>
                ) : null}
                {rawData?.room_keys?.length ? (
                  <div style={{marginTop:4}}><strong>room keys :</strong> {rawData.room_keys.join(", ")}</div>
                ) : null}
              </div>
            </details>
          ) : null}
        </div>

        {/* Footer */}
        <div style={{padding:"14px 20px",borderTop:"1px solid #eef2f7",display:"flex",justifyContent:"flex-end",gap:8,background:"#fff"}}>
          <button onClick={onClose}
            style={{background:"#eef2f7",color:"#555",border:"none",borderRadius:7,padding:"9px 16px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:DS.fontBody}}>
            Annuler
          </button>
          <button onClick={handleApply} disabled={checkedCount === 0}
            style={{background: checkedCount === 0 ? '#cbd5e1' : DS.navy,color:"#fff",border:"none",borderRadius:7,padding:"9px 18px",fontSize:12,fontWeight:700,cursor: checkedCount === 0 ? 'not-allowed' : 'pointer',fontFamily:DS.fontBody}}>
            Importer {checkedCount > 0 ? `${checkedCount} changement${checkedCount>1?'s':''}` : ''}
          </button>
        </div>
      </div>
    </div>
  )
}
